import { AutopilotTaskDto } from '@urbanx/agx.activities.client'
import {
  AgxCollapsible,
  AgxEmptyState,
  AgxIconTask,
  AgxProgressBar,
  AgxSpacer,
  AgxTypography,
} from '@urbanx/agx.ui-components2'
import { ReactNode, useMemo } from 'react'
import {
  getMissingDueDateText,
  getStepCompletionPercentage,
  getStepsCompletionInfo,
  getTaskDomId,
  getTaskLabel,
} from 'utils/tasks'
import TaskAssigneeSelect from './TaskAssigneeSelect'
import TaskDueDateBadge from './TaskDueDateBadge'
import TaskStepItem from './TaskStepItem'
import {
  AssigneeOption,
  AssignTaskHandler,
  mapTaskTypeToIconType,
  ToggleTaskStepsHandler,
} from './taskCollapsibleShared'

interface TaskCollapsibleProps {
  task: AutopilotTaskDto
  assigneeOptions: AssigneeOption[]
  assignTask: AssignTaskHandler
  toggleTaskSteps: ToggleTaskStepsHandler
  renderTaskContent?: (props: {
    task: AutopilotTaskDto
    children: ReactNode
  }) => ReactNode
}

const TaskCollapsible = ({
  task,
  assigneeOptions,
  assignTask,
  toggleTaskSteps,
  renderTaskContent,
}: TaskCollapsibleProps) => {
  if (!task.category) return null

  const taskSteps = task.taskSteps ?? []
  const { allStepsCompleted, latestCompletedDate } = useMemo(
    () => getStepsCompletionInfo([task]),
    [taskSteps]
  )
  const isUnassigned = useMemo(
    () =>
      (!task.assignedTo || task.assignedTo === 'Unassigned') && !task.isPending,
    [task.assignedTo, task.isPending]
  )

  const assigneeSelectId = getTaskDomId('assignToUserChip', task)

  const content = isUnassigned ? (
    <AgxSpacer
      direction="horizontal"
      fill
      contentAlignment="center"
      contentJustification="center"
    >
      <AgxEmptyState
        variant="advanced"
        glyphType="locked"
        actionButtons={[]}
        label="Assign task to unlock"
      />
    </AgxSpacer>
  ) : (
    <AgxSpacer direction="vertical" fill gap="spacing-lg">
      {taskSteps.map((step) => (
        <TaskStepItem
          key={`${step.order}-${step.name}`}
          step={step}
          allStepsCompleted={allStepsCompleted}
          taskId={task.id}
          taskType={task.category?.taskType}
          toggleTaskSteps={toggleTaskSteps}
        />
      ))}
    </AgxSpacer>
  )

  return (
    <AgxCollapsible
      key={task.id}
      open={!task.isCompleted}
      iconPosition="none"
      enableToggle={true}
      smoothTransition
      variant={'withoutDivider'}
      title={''}
      contentPadding={{ left: 'xl', bottom: 'xl', right: 'xl' }}
      customTitle={
        <AgxSpacer direction="vertical" fill gap="spacing-ml">
          <AgxSpacer
            direction="horizontal"
            fill
            contentAlignment="center"
            contentJustification="space-between"
          >
            <AgxSpacer
              direction="horizontal"
              gap="spacing-lg"
              contentAlignment="center"
            >
              <AgxIconTask
                type={mapTaskTypeToIconType(task.category?.taskType)}
                iconColor="black"
                height={32}
                width={32}
                bgColor="var(--bg-progress)"
              />
              <AgxTypography id={'title'} type={'heading'} size={'medium'}>
                {getTaskLabel(task)}
              </AgxTypography>
            </AgxSpacer>
            <AgxSpacer
              direction="horizontal"
              gap="spacing-lg"
              contentJustification="end"
              contentAlignment="center"
            >
              <div onClick={(e) => e.stopPropagation()}>
                {!allStepsCompleted ? (
                  <TaskAssigneeSelect
                    id={assigneeSelectId}
                    value={task.assignedTo}
                    onAssign={(userId) => {
                      if (!task.id) return

                      assignTask({
                        taskId: task.id,
                        userId,
                      })
                    }}
                    options={assigneeOptions}
                  />
                ) : (
                  <AgxTypography
                    id={`${assigneeSelectId}-completed`}
                    type="caption"
                    size="medium"
                  >
                    {assigneeOptions.find(
                      (o) => o.value === task.assignedTo
                    )?.label ?? task.assignedTo}
                  </AgxTypography>
                )}
              </div>
              <TaskDueDateBadge
                dueDate={(task.dueDate ?? '') as string}
                isCompleted={allStepsCompleted}
                completedDate={latestCompletedDate}
                missingDateText={getMissingDueDateText(task)}
              />
            </AgxSpacer>
          </AgxSpacer>
          {task.isCompleted ? (
            <AgxTypography
              id={'txtComplete'}
              type={'caption'}
              size={'medium'}
              color="var(--text-success)"
            >
              Complete! 🎉
            </AgxTypography>
          ) : (
            <AgxProgressBar
              variant="slim"
              id="progress"
              percentageProgressPosition="left"
              mode="dark"
              progress={getStepCompletionPercentage(taskSteps)}
            />
          )}
        </AgxSpacer>
      }
      content={
        <AgxSpacer direction="horizontal" fill>
          <AgxSpacer direction="vertical" fill gap="spacing-2xl">
            {renderTaskContent
              ? renderTaskContent({ task, children: content })
              : content}
          </AgxSpacer>
        </AgxSpacer>
      }
    />
  )
}

export default TaskCollapsible
